/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { HouseholdService } from 'app/cd/services/household-service';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class HouseholdHeader extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    protected householdId: string | null = null;

    protected household: CD.Household | null = null;

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;

    //#region Household Members to Display on table
    protected householdMembers: CD.Member[] = []
    protected sorting = new Sorting();
    //#endregion Household Members to Display on table

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private householdService: HouseholdService,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();
    }

    protected attached(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        // Default Sort
        this.sorting = new Sorting();
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];

        await Promise.all([
            this.loadHouseholdInfo(),
            this.loadHouseholdMembers()
        ]);
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.loadHouseholdMembers();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected householdIdChanged(newValue: string, oldValue: string): void {
        if(newValue !== oldValue) {
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    ////****************** Service Calls ********************************************////
    // Load Household information
    @apiCall
    private async loadHouseholdInfo(): Promise<void> {
        if(!this.householdId) return;

        try {
            this.state.beginLoad();

            const response = await this.householdService.loadHousehold(this.householdId);
            this.household = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    // Load Household Members
    @apiCall
    private async loadHouseholdMembers(): Promise<void> {
        if(!this.householdId) return;

        try {
            this.state.beginLoad();
            const includes: string[] = [
                'primaryPlan.lineOfBusiness',
                'primaryPlan.healthPlan'
            ];

            const options: Partial<IQueryLoadOptions> = {
                sorting: this.sorting,
                includeInactive: false,
                all: true
            };

            const response = await this.householdService.loadHouseholdMembers(this.householdId, options, includes);
            this.householdMembers = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
